<template>
  <Card class="mb-4">
    <template #header>
      <div class="flex align-items-center justify-content-between">
        <div class="flex align-items-center">
          <i class="pi pi-list mr-2"></i>
          <h5 class="m-0">Transaction Items</h5>
        </div>
        <div class="flex gap-2">
          <Button
            v-if="!readonly"
            :label="isBulkEditMode ? 'Cancel' : 'Bulk Edit'"
            :icon="isBulkEditMode ? 'pi pi-times' : 'pi pi-pencil'"
            class="p-button-sm"
            :class="isBulkEditMode ? 'p-button-secondary' : 'p-button-outlined'"
            @click="toggleBulkEditMode"
          />
          <Button
            v-if="!readonly"
            label="Add Item"
            icon="pi pi-plus"
            class="p-button-sm"
            @click="$emit('add-item')"
          />
        </div>
      </div>
    </template>
    <template #content>
      <div v-if="items.length === 0" class="flex flex-column align-items-center py-5">
        <i class="pi pi-shopping-cart text-6xl text-200 mb-4"></i>
        <p class="text-500">No items added. Click 'Add Item' to begin.</p>
      </div>

      <!-- Using responsive layout "stack" for mobile views -->
      <DataTable
        v-else
        :value="items"
        class="p-datatable-sm"
        responsiveLayout="stack"
        breakpoint="960px"
        :rowHover="true"
      >
        <Column field="commodity_type_id" header="Commodity">
          <template #body="slotProps">
            {{
              getCommodityName(slotProps.data.commodity_type_id) || slotProps.data.commodity_name
            }}
          </template>
        </Column>
        <Column header="Image" :exportable="false" style="min-width: 4rem">
          <template #body="slotProps">
            <div class="flex justify-content-start">
              <Button
                :icon="'pi pi-camera'"
                :class="[
                  'p-button-rounded p-button-sm p-button-text ml-2',
                  hasImage(slotProps.data) ? 'p-button-success' : 'p-button-danger',
                ]"
                @click="showImageDialog(slotProps.data)"
                :aria-label="hasImage(slotProps.data) ? 'View Image' : 'Image Missing'"
                v-tooltip="hasImage(slotProps.data) ? 'View Image' : 'Image Missing'"
              />
            </div>
          </template>
        </Column>
        <Column field="price_per_lb" header="Price per lb ($)">
          <template #body="slotProps">
            <div v-if="isBulkEditMode" class="flex justify-content-center">
              <InputNumber
                :model-value="getBulkEditValue(items.indexOf(slotProps.data), 'price_per_lb')"
                @update:model-value="
                  setBulkEditValue(items.indexOf(slotProps.data), 'price_per_lb', $event)
                "
                mode="currency"
                currency="USD"
                locale="en-US"
                :min-fraction-digits="2"
                :max-fraction-digits="4"
                class="w-full"
              />
            </div>
            <span v-else>{{ formatCurrency(slotProps.data.price_per_lb) }}</span>
          </template>
        </Column>
        <Column field="weight.gross" header="Gross Weight (lbs)">
          <template #body="slotProps">
            <div v-if="isBulkEditMode" class="flex justify-content-center">
              <InputNumber
                :model-value="getBulkEditValue(items.indexOf(slotProps.data), 'gross_weight')"
                @update:model-value="
                  setBulkEditValue(items.indexOf(slotProps.data), 'gross_weight', $event)
                "
                :min-fraction-digits="2"
                :max-fraction-digits="2"
                class="w-full"
              />
            </div>
            <span v-else>{{ formatWeight(slotProps.data.weight.gross) }}</span>
          </template>
        </Column>
        <Column field="weight.tare" header="Tare Weight (lbs)">
          <template #body="slotProps">
            <div v-if="isBulkEditMode" class="flex justify-content-center">
              <InputNumber
                :model-value="getBulkEditValue(items.indexOf(slotProps.data), 'tare_weight')"
                @update:model-value="
                  setBulkEditValue(items.indexOf(slotProps.data), 'tare_weight', $event)
                "
                :min-fraction-digits="2"
                :max-fraction-digits="2"
                class="w-full"
              />
            </div>
            <span v-else>{{ formatWeight(slotProps.data.weight.tare) }}</span>
          </template>
        </Column>
        <Column field="weight.net" header="Net Weight (lbs)">
          <template #body="slotProps">
            {{ formatWeight(slotProps.data.weight.net) }}
          </template>
        </Column>
        <Column field="total_price" header="Total ($)">
          <template #body="slotProps">
            {{ formatCurrency(calculateLineTotal(slotProps.data)) }}
          </template>
        </Column>
        <Column v-if="!readonly" header="Actions" :exportable="false" style="min-width: 8rem">
          <template #body="slotProps">
            <div class="flex gap-2 justify-content-center">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-sm p-button-text"
                @click="$emit('edit-item', items.indexOf(slotProps.data))"
                aria-label="Edit Item"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-sm p-button-text p-button-danger"
                @click="$emit('remove-item', items.indexOf(slotProps.data))"
                aria-label="Remove Item"
              />
            </div>
          </template>
        </Column>
      </DataTable>

      <!-- Bulk Edit Save Button -->
      <div v-if="isBulkEditMode && items.length > 0" class="flex justify-content-end mt-3">
        <Button
          label="Save Changes"
          icon="pi pi-check"
          class="p-button-success"
          @click="saveBulkChanges"
        />
      </div>

      <!-- Mobile summary - only shows on small screens -->
      <div
        class="surface-card p-4 shadow-2 border-round mt-3 block md:hidden"
        v-if="items.length > 0"
      >
        <div class="flex justify-content-between align-items-center mb-3">
          <span class="text-900 font-medium">Total Items: {{ items.length }}</span>
          <span class="text-900 font-bold">{{ formatCurrency(totalAmount) }}</span>
        </div>
      </div>
    </template>
  </Card>
  <!-- Image Dialog -->
  <Dialog
    v-model:visible="imageDialogVisible"
    :style="{ width: '350px' }"
    header="Item Image"
    :modal="true"
    :closable="true"
  >
    <div class="flex justify-content-center">
      <img
        v-if="selectedItem && (selectedItem.image_preview || selectedItem.image_url)"
        :src="selectedItem.image_preview || selectedItem.image_url || ''"
        alt="Item Image"
        class="w-full max-h-64 object-contain"
      />
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import type { TransactionItem } from '@/types/Transaction'
import type { CommodityType } from '@/types/CommodityType'

const props = defineProps({
  items: {
    type: Array as () => TransactionItem[],
    required: true,
  },
  commodityTypes: {
    type: Array as () => CommodityType[],
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['add-item', 'edit-item', 'remove-item', 'bulk-update'])

const imageDialogVisible = ref(false)
const selectedItem = ref<TransactionItem | null>(null)
const isBulkEditMode = ref(false)
const bulkEditValues = ref<
  Record<
    number,
    {
      price_per_lb: number | null
      gross_weight: number | null
      tare_weight: number | null
    }
  >
>({})

// Function to show image dialog
function showImageDialog(item: TransactionItem) {
  selectedItem.value = item
  imageDialogVisible.value = true
}

// Bulk edit functions
function toggleBulkEditMode() {
  isBulkEditMode.value = !isBulkEditMode.value
  if (isBulkEditMode.value) {
    // Initialize bulk edit values with current item values
    bulkEditValues.value = {}
    props.items.forEach((item, index) => {
      bulkEditValues.value[index] = {
        price_per_lb: item.price_per_lb || null,
        gross_weight: item.weight?.gross || null,
        tare_weight: item.weight?.tare || null,
      }
    })
  } else {
    // Reset bulk edit values when canceling
    bulkEditValues.value = {}
  }
}

function saveBulkChanges() {
  const updates: Array<{ index: number; updates: any }> = []

  props.items.forEach((item, index) => {
    const itemValues = bulkEditValues.value[index]
    if (!itemValues) return

    const itemUpdates: any = {}
    let hasChanges = false

    // Check if price_per_lb changed
    if (itemValues.price_per_lb !== null && itemValues.price_per_lb !== item.price_per_lb) {
      itemUpdates.price_per_lb = itemValues.price_per_lb
      hasChanges = true
    }

    // Check if gross weight changed
    if (itemValues.gross_weight !== null && itemValues.gross_weight !== item.weight?.gross) {
      itemUpdates.weight = {
        ...item.weight,
        gross: itemValues.gross_weight,
        net: itemValues.gross_weight - (itemValues.tare_weight || item.weight?.tare || 0),
      }
      hasChanges = true
    }

    // Check if tare weight changed
    if (itemValues.tare_weight !== null && itemValues.tare_weight !== item.weight?.tare) {
      itemUpdates.weight = {
        ...item.weight,
        tare: itemValues.tare_weight,
        net: (itemValues.gross_weight || item.weight?.gross || 0) - itemValues.tare_weight,
      }
      hasChanges = true
    }

    // Only include items that have actual changes
    if (hasChanges) {
      updates.push({ index, updates: itemUpdates })
    }
  })

  if (updates.length > 0) {
    emit('bulk-update', updates)
  }
  toggleBulkEditMode()
}

function hasImage(item: TransactionItem) {
  return !!(item.image_preview || item.image_url)
}

// Helper functions for bulk edit values
function getBulkEditValue(index: number, field: 'price_per_lb' | 'gross_weight' | 'tare_weight') {
  return bulkEditValues.value[index]?.[field] || null
}

function setBulkEditValue(
  index: number,
  field: 'price_per_lb' | 'gross_weight' | 'tare_weight',
  value: any,
) {
  if (!bulkEditValues.value[index]) {
    bulkEditValues.value[index] = {
      price_per_lb: null,
      gross_weight: null,
      tare_weight: null,
    }
  }
  bulkEditValues.value[index][field] = value
}

// Helper functions
function getCommodityName(commodityId: string): string {
  const commodity = props.commodityTypes.find((c) => c.id === commodityId)
  return commodity ? commodity.name : ''
}

function calculateLineTotal(item: TransactionItem): number {
  if (item.total_price) return item.total_price

  const price = Number(item.price_per_lb) || 0
  const weight = Number(item.weight?.net) || 0
  return parseFloat((price * weight).toFixed(2))
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

function formatWeight(value: number | null): string {
  if (value === null || value === undefined) return 'N/A'
  return `${value.toFixed(2)} lbs`
}

// Compute total amount for mobile summary
const totalAmount = computed(() => {
  return props.items.reduce((total, item) => {
    return total + calculateLineTotal(item)
  }, 0)
})
</script>

<style scoped>
/* Make buttons larger and more touch-friendly on mobile */
@media screen and (max-width: 768px) {
  :deep(.p-button.p-button-sm) {
    padding: 0.5rem !important;
    width: 2.5rem !important;
    height: 2.5rem !important;
  }

  :deep(.p-button .p-button-icon) {
    font-size: 1rem;
  }

  /* Improve stack layout spacing */
  :deep(.p-datatable-tbody > tr > td .p-column-title) {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  :deep(.p-datatable.p-datatable-sm .p-datatable-tbody > tr > td) {
    padding: 0.75rem !important;
  }
}
</style>
